import { useState } from "react";
import { api } from "@/lib/api";
import { Download, Check, FileText } from "lucide-react";

const GUIDE_FILENAME = "pistack-app-development-guide.md";

export function DocsPage() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/docs/app-development-guide", {
        responseType: "text",
        headers: { Accept: "text/markdown" },
      });
      const blob = new Blob([data], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = GUIDE_FILENAME;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download guide:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-8 pt-8 pb-12 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-surface-900">Documentation</h1>
          <p className="mt-1 text-sm text-surface-500">
            Guides and references for building apps that deploy to PiStack-managed devices.
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {/* App Development Guide */}
        <div className="bg-surface-0 rounded-xl border border-surface-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <FileText size={24} className="text-brand-500" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-surface-900">
                App Development Guide
              </h2>
              <p className="mt-1 text-sm text-surface-500 leading-relaxed">
                Complete reference for building and deploying applications to Raspberry Pi
                devices managed by PiStack Manager. Covers the tarball contract, React SPA
                setup with a zero-dependency Node.js server, GitHub Actions CI/CD pipeline,
                health checks, WebSocket support, and the PiStack Manager API.
              </p>
              <p className="mt-3 text-sm text-surface-500">
                Download this file and hand it to Claude (or any AI assistant) at the start
                of a new conversation to scaffold a Pi-deployable project from scratch.
              </p>

              <div className="mt-4">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer"
                >
                  <Download size={16} />
                  {downloading ? "Downloading..." : "Download .md"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* What's in the guide */}
        <div className="bg-surface-0 rounded-xl border border-surface-200 p-6">
          <h3 className="text-sm font-semibold text-surface-800 mb-4">What's covered</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <GuideSection title="Tarball Contract" description="Required file structure, package.json format, and what the Pi agent expects" />
            <GuideSection title="React SPA Setup" description="Vite build config, zero-dep Node.js static server with SPA fallback routing" />
            <GuideSection title="Pi Server" description="Health check endpoint, cache headers, MIME types, graceful shutdown" />
            <GuideSection title="Build & Package" description="Script to create a Pi-ready tarball from your Vite build output" />
            <GuideSection title="GitHub Actions CI/CD" description="Workflow that builds, uploads to S3, and triggers PiStack deploys on push" />
            <GuideSection title="API Routes" description="Pattern for adding backend API routes alongside the SPA" />
            <GuideSection title="WebSocket Support" description="Real-time features using the ws package" />
            <GuideSection title="API Reference" description="All PiStack Manager API endpoints with auth requirements" />
          </div>
        </div>

        {/* Usage tip */}
        <div className="px-4 py-3 rounded-lg bg-blue-100 border border-blue-400/20 text-sm text-surface-600 leading-relaxed">
          <strong className="text-surface-700">Tip:</strong> When starting a new project, paste the entire
          markdown file into a Claude conversation and say something like: "I want to build a React dashboard
          that runs on my Raspberry Pi. Use this guide to set up the project." Claude will scaffold the full
          project structure, server, build pipeline, and deploy workflow.
        </div>
      </div>
    </div>
  );
}

function GuideSection({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-3 rounded-lg bg-surface-50 border border-surface-200">
      <div className="text-sm font-medium text-surface-800">{title}</div>
      <div className="text-xs text-surface-500 mt-1">{description}</div>
    </div>
  );
}
